<template>
  <div class="collision-lab">
    <h1 class="lab-title">Лаборатория столкновений</h1>
    <p class="lab-subtitle">
      Изучение законов сохранения импульса и энергии при центральном столкновении двух тел
    </p>

    <div class="lab-layout">
      <!-- Canvas -->
      <div class="canvas-wrapper" ref="canvasWrapper">
        <canvas ref="simCanvas" class="sim-canvas"></canvas>
        <div class="legend">
          <div class="legend-item"><span class="dot" style="background:#ef4444"></span> Тело 1</div>
          <div class="legend-item"><span class="dot" style="background:#3b82f6"></span> Тело 2</div>
          <div class="legend-item"><span class="arrow" style="color:#22c55e">→</span> Направление скорости</div>
        </div>
      </div>

      <!-- Панель управления -->
      <div class="controls-panel">
        <div class="control-group">
          <h3>Тело 1</h3>
          <label>Масса m₁ (кг)</label>
          <input type="range" min="1" max="20" step="0.5" v-model.number="m1" />
          <span class="val">{{ m1.toFixed(1) }}</span>

          <label>Скорость u₁ (м/с)</label>
          <input type="range" min="-10" max="10" step="0.5" v-model.number="u1" />
          <span class="val">{{ u1.toFixed(1) }}</span>
        </div>

        <div class="control-group">
          <h3>Тело 2</h3>
          <label>Масса m₂ (кг)</label>
          <input type="range" min="1" max="20" step="0.5" v-model.number="m2" />
          <span class="val">{{ m2.toFixed(1) }}</span>

          <label>Скорость u₂ (м/с)</label>
          <input type="range" min="-10" max="10" step="0.5" v-model.number="u2" />
          <span class="val">{{ u2.toFixed(1) }}</span>
        </div>

        <div class="control-group wide">
          <h3>Параметры столкновения</h3>
          <label>Коэффициент восстановления k (0 = неупругое, 1 = упругое)</label>
          <input type="range" min="0" max="1" step="0.01" v-model.number="restitution" />
          <span class="val">{{ restitution.toFixed(2) }}</span>

          <div class="checkboxes">
            <label><input type="checkbox" v-model="showGrid" /> Сетка</label>
            <label><input type="checkbox" v-model="showVectors" /> Векторы скорости</label>
            <label><input type="checkbox" v-model="showTrails" /> Следы</label>
          </div>
        </div>

        <div class="buttons">
          <button class="btn-primary" @click="togglePlay">
            {{ isRunning ? 'Пауза' : 'Старт' }}
          </button>
          <button class="btn-secondary" @click="reset">Сброс</button>
          <button class="btn-secondary" @click="step" :disabled="isRunning">Шаг +1</button>
        </div>
      </div>
    </div>

    <!-- Таблица результатов -->
    <div class="results-panel" v-if="hasCollided || resultVisible">
      <h3>Результаты столкновения</h3>
      <div class="results-table-wrapper">
        <table class="results-table">
          <thead>
            <tr>
              <th>Параметр</th>
              <th>До столкновения</th>
              <th>После столкновения</th>
              <th>Изменение</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Скорость тела 1, м/с</td>
              <td>{{ u1.toFixed(2) }}</td>
              <td>{{ v1.toFixed(2) }}</td>
              <td :class="deltaClass(v1 - u1)">{{ fmtDelta(v1 - u1) }}</td>
            </tr>
            <tr>
              <td>Скорость тела 2, м/с</td>
              <td>{{ u2.toFixed(2) }}</td>
              <td>{{ v2.toFixed(2) }}</td>
              <td :class="deltaClass(v2 - u2)">{{ fmtDelta(v2 - u2) }}</td>
            </tr>
            <tr>
              <td>Импульс системы, кг·м/с</td>
              <td>{{ pBefore.toFixed(2) }}</td>
              <td>{{ pAfter.toFixed(2) }}</td>
              <td :class="deltaClass(pAfter - pBefore)">{{ fmtDelta(pAfter - pBefore) }}</td>
            </tr>
            <tr>
              <td>Кинетическая энергия, Дж</td>
              <td>{{ ekBefore.toFixed(2) }}</td>
              <td>{{ ekAfter.toFixed(2) }}</td>
              <td :class="deltaClass(ekAfter - ekBefore)">{{ fmtDelta(ekAfter - ekBefore) }}</td>
            </tr>
            <tr>
              <td>Относительная скорость разлёта, м/с</td>
              <td>—</td>
              <td>{{ Math.abs(v2 - v1).toFixed(2) }}</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="formula-box">
        <strong>Используемые формулы:</strong>
        <code class="formula">
          v₁ = (m₁u₁ + m₂u₂ − m₂·k·(u₁ − u₂)) / (m₁ + m₂) &nbsp;&nbsp; v₂ = (m₁u₁ + m₂u₂ + m₁·k·(u₁ − u₂)) / (m₁ + m₂)
        </code>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';

const canvasWrapper = ref(null);
const simCanvas = ref(null);

// Параметры тел
const m1 = ref(4);
const m2 = ref(2);
const u1 = ref(3);
const u2 = ref(-2);
const restitution = ref(1.0);

// Визуализация
const showGrid = ref(true);
const showVectors = ref(true);
const showTrails = ref(true);

// Состояние симуляции
const isRunning = ref(false);
const hasCollided = ref(false);
const resultVisible = ref(false);

// Результаты
const v1 = ref(0);
const v2 = ref(0);

const pBefore = computed(() => m1.value * u1.value + m2.value * u2.value);
const pAfter = computed(() => m1.value * v1.value + m2.value * v2.value);
const ekBefore = computed(() => 0.5 * m1.value * u1.value ** 2 + 0.5 * m2.value * u2.value ** 2);
const ekAfter = computed(() => 0.5 * m1.value * v1.value ** 2 + 0.5 * m2.value * v2.value ** 2);

let ctx = null;
let animId = null;
let lastTime = 0;

// Физические константы для визуализации
const PIXELS_PER_METER = 40; // масштаб
const TIME_SCALE = 1.0;      // замедление/ускорение

// Состояние шаров
const ball1 = ref({ x: 3, vx: 0 });
const ball2 = ref({ x: 12, vx: 0 });
const trail1 = ref([]);
const trail2 = ref([]);

let collisionX = null;
let collisionTime = 0;
let simTime = 0;

function getRadius(mass) {
  // r ~ cube root of mass, clamped
  const r = 0.3 * Math.cbrt(mass);
  return Math.max(0.25, Math.min(1.2, r));
}

function calculateFinalVelocities() {
  const m1v = m1.value;
  const m2v = m2.value;
  const k = restitution.value;
  const u1v = u1.value;
  const u2v = u2.value;

  v1.value = (m1v * u1v + m2v * u2v - m2v * k * (u1v - u2v)) / (m1v + m2v);
  v2.value = (m1v * u1v + m2v * u2v + m1v * k * (u1v - u2v)) / (m1v + m2v);
}

function resizeCanvas() {
  const canvas = simCanvas.value;
  if (!canvas || !canvasWrapper.value) return;
  const rect = canvasWrapper.value.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.floor(rect.width * dpr);
  canvas.height = Math.floor(rect.height * dpr);
  canvas.style.width = rect.width + 'px';
  canvas.style.height = rect.height + 'px';
  ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  draw();
}

function worldToScreen(wx, wy = 0) {
  const canvas = simCanvas.value;
  if (!canvas) return { x: 0, y: 0 };
  const marginX = 60;


  const x = marginX + wx * PIXELS_PER_METER;
  const y = canvas.clientHeight / 2 + wy * PIXELS_PER_METER;
  return { x, y };
}

function drawGrid() {
  if (!showGrid.value) return;
  const canvas = simCanvas.value;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;

  ctx.save();
  ctx.strokeStyle = 'rgba(0,0,0,0.06)';
  ctx.lineWidth = 1;

  // Вертикальные линии каждый метр
  for (let mx = 0; mx * PIXELS_PER_METER < w; mx++) {
    const p = worldToScreen(mx);
    ctx.beginPath();
    ctx.moveTo(p.x, 20);
    ctx.lineTo(p.x, h - 20);
    ctx.stroke();
    ctx.fillStyle = '#666';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(mx + ' м', p.x, h - 5);
  }

  // Горизонтальная ось
  const cy = h / 2;
  ctx.strokeStyle = 'rgba(0,0,0,0.15)';
  ctx.beginPath();
  ctx.moveTo(30, cy);
  ctx.lineTo(w - 30, cy);
  ctx.stroke();

  ctx.restore();
}

function drawArrow(fromX, fromY, vx, color, label) {
  if (!showVectors.value) return;
  const scale = 8; // px per m/s
  const len = vx * scale;
  if (Math.abs(len) < 2) return;

  const toX = fromX + len;
  const toY = fromY;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2;

  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();

  // Arrowhead
  const head = 6;
  const angle = len > 0 ? 0 : Math.PI;
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(toX - head * Math.cos(angle - 0.4), toY - head * Math.sin(angle - 0.4));
  ctx.lineTo(toX - head * Math.cos(angle + 0.4), toY - head * Math.sin(angle + 0.4));
  ctx.closePath();
  ctx.fill();

  // Label
  ctx.font = 'bold 12px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(label, (fromX + toX) / 2, fromY - 8);
  ctx.restore();
}

function drawBall(x, y, r, color, label, massLabel) {
  const p = worldToScreen(x);
  const screenR = r * PIXELS_PER_METER;

  ctx.save();
  // Shadow
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 4;

  // Gradient for 3D look
  const grad = ctx.createRadialGradient(
    p.x - screenR * 0.3, p.y - screenR * 0.3, screenR * 0.1,
    p.x, p.y, screenR
  );
  grad.addColorStop(0, lighten(color, 40));
  grad.addColorStop(0.5, color);
  grad.addColorStop(1, darken(color, 30));

  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(p.x, p.y, screenR, 0, Math.PI * 2);
  ctx.fill();

  // Border
  ctx.strokeStyle = darken(color, 40);
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.restore();

  // Label inside ball
  ctx.save();
  ctx.fillStyle = 'white';
  ctx.font = 'bold 13px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 3;
  ctx.fillText(label, p.x, p.y);
  ctx.restore();

  // Mass label below
  ctx.save();
  ctx.fillStyle = '#444';
  ctx.font = '11px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(massLabel, p.x, p.y + screenR + 14);
  ctx.restore();
}

function lighten(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
  const B = Math.min(255, (num & 0x0000ff) + amt);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

function darken(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
  const B = Math.max(0, (num & 0x0000ff) - amt);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

function drawTrails() {
  if (!showTrails.value) return;
  const drawTrail = (trail, color) => {
    if (trail.length < 2) return;
    ctx.save();
    ctx.strokeStyle = color + '66'; // 40% opacity
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    for (let i = 0; i < trail.length; i++) {
      const p = worldToScreen(trail[i]);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  };
  drawTrail(trail1.value, '#ef4444');
  drawTrail(trail2.value, '#3b82f6');
}

function drawCollisionEffect() {
  if (collisionX == null) return;
  const age = simTime - collisionTime;
  if (age > 0.6) {
    collisionX = null;
    return;
  }
  const p = worldToScreen(collisionX);
  const progress = age / 0.6;
  const radius = (1 - progress) * 80;
  const alpha = (1 - progress) * 0.4;

  ctx.save();
  ctx.strokeStyle = `rgba(255, 200, 50, ${alpha})`;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function draw() {
  const canvas = simCanvas.value;
  if (!ctx || !canvas) return;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  ctx.clearRect(0, 0, w, h);

  drawGrid();
  drawTrails();

  const r1 = getRadius(m1.value);
  const r2 = getRadius(m2.value);

  drawBall(ball1.value.x, 0, r1, '#ef4444', '1', `m₁=${m1.value.toFixed(1)}кг`);
  drawBall(ball2.value.x, 0, r2, '#3b82f6', '2', `m₂=${m2.value.toFixed(1)}кг`);

  // Vectors
  const p1 = worldToScreen(ball1.value.x);
  const p2 = worldToScreen(ball2.value.x);
  const currentV1 = hasCollided.value ? v1.value : u1.value;
  const currentV2 = hasCollided.value ? v2.value : u2.value;
  drawArrow(p1.x, p1.y - r1 * PIXELS_PER_METER - 10, currentV1, '#22c55e', `v₁=${currentV1.toFixed(1)}`);
  drawArrow(p2.x, p2.y - r2 * PIXELS_PER_METER - 10, currentV2, '#22c55e', `v₂=${currentV2.toFixed(1)}`);

  drawCollisionEffect();
}

function reset() {
  stop();
  simTime = 0;
  collisionX = null;
  hasCollided.value = false;
  resultVisible.value = false;
  trail1.value = [];
  trail2.value = [];

  // Начальные позиции: шар 1 слева, шар 2 справа, с отступом
  ball1.value.x = 3;
  ball2.value.x = 17;
  ball1.value.vx = u1.value;
  ball2.value.vx = u2.value;

  calculateFinalVelocities();
  draw();
}

function step() {
  updatePhysics(1 / 60);
  draw();
}

function updatePhysics(dt) {
  const r1 = getRadius(m1.value);
  const r2 = getRadius(m2.value);

  const b1 = ball1.value;
  const b2 = ball2.value;

  if (!hasCollided.value) {
    b1.x += b1.vx * dt;
    b2.x += b2.vx * dt;

    // Проверка столкновения
    const dist = Math.abs(b2.x - b1.x);
    const minDist = r1 + r2;
    if (dist <= minDist) {
      hasCollided.value = true;
      collisionX = (b1.x * r2 + b2.x * r1) / (r1 + r2); // приближённо
      collisionTime = simTime;
      b1.vx = v1.value;
      b2.vx = v2.value;
      resultVisible.value = true;
    }
  } else {
    b1.x += b1.vx * dt;
    b2.x += b2.vx * dt;
  }

  // Границы canvas (отскок от стен)
  const margin = 1.5;
  if (b1.x < r1 + margin) { b1.x = r1 + margin; b1.vx *= -0.5; }
  if (b1.x > 25 - r1 - margin) { b1.x = 25 - r1 - margin; b1.vx *= -0.5; }
  if (b2.x < r2 + margin) { b2.x = r2 + margin; b2.vx *= -0.5; }
  if (b2.x > 25 - r2 - margin) { b2.x = 25 - r2 - margin; b2.vx *= -0.5; }

  // Trails
  if (showTrails.value && isRunning.value) {
    if (trail1.value.length === 0 || Math.abs(b1.x - trail1.value[trail1.value.length - 1]) > 0.05) {
      trail1.value.push(b1.x);
      if (trail1.value.length > 200) trail1.value.shift();
    }
    if (trail2.value.length === 0 || Math.abs(b2.x - trail2.value[trail2.value.length - 1]) > 0.05) {
      trail2.value.push(b2.x);
      if (trail2.value.length > 200) trail2.value.shift();
    }
  }

  simTime += dt;
}

function loop(timestamp) {
  if (!isRunning.value) return;
  if (!lastTime) lastTime = timestamp;
  const rawDt = (timestamp - lastTime) / 1000;
  lastTime = timestamp;

  // Clamp dt to avoid spiral of death
  const dt = Math.min(rawDt, 0.05) * TIME_SCALE;

  updatePhysics(dt);
  draw();

  animId = requestAnimationFrame(loop);
}

function togglePlay() {
  if (isRunning.value) {
    stop();
  } else {
    start();
  }
}

function start() {
  if (isRunning.value) return;
  // Если уже было столкновение и мы на границе — сбросить
  if (hasCollided.value && (ball1.value.x < 2 || ball2.value.x > 23)) {
    reset();
  }
  isRunning.value = true;
  lastTime = 0;
  animId = requestAnimationFrame(loop);
}

function stop() {
  isRunning.value = false;
  if (animId) cancelAnimationFrame(animId);
  animId = null;
  lastTime = 0;
}

function deltaClass(val) {
  if (Math.abs(val) < 0.01) return 'zero';
  return val > 0 ? 'positive' : 'negative';
}

function fmtDelta(val) {
  const s = (val >= 0 ? '+' : '') + val.toFixed(2);
  return s;
}

// Слушатели изменений параметров
watch([m1, m2, u1, u2, restitution], () => {
  calculateFinalVelocities();
  if (!isRunning.value) {
    ball1.value.vx = u1.value;
    ball2.value.vx = u2.value;
    draw();
  }
});

watch([showGrid, showVectors, showTrails], () => {
  draw();
});

onMounted(() => {
  nextTick(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    calculateFinalVelocities();
    reset();
  });
});

onUnmounted(() => {
  stop();
  window.removeEventListener('resize', resizeCanvas);
});
</script>

<style scoped>
.collision-lab {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #1f2937;
}

.lab-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 4px;
  color: #111827;
}

.lab-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 20px;
}

.lab-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 20px;
  align-items: start;
}

@media (max-width: 900px) {
  .lab-layout {
    grid-template-columns: 1fr;
  }
}

.canvas-wrapper {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 12px;
}

.sim-canvas {
  width: 100%;
  height: 320px;
  display: block;
  border-radius: 8px;
  background: #fafafa;
}

.legend {
  display: flex;
  gap: 16px;
  margin-top: 10px;
  font-size: 0.85rem;
  color: #4b5563;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.arrow {
  font-weight: bold;
  font-size: 1.1rem;
}

.controls-panel {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.control-group {
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
}

.control-group:last-of-type {
  border-bottom: none;
}

.control-group h3 {
  font-size: 0.95rem;
  margin: 0 0 10px 0;
  color: #374151;
}

.control-group label {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 4px;
  margin-top: 10px;
}

.control-group input[type="range"] {
  width: 100%;
  accent-color: #2563eb;
}

.control-group .val {
  display: inline-block;
  font-weight: 600;
  font-size: 0.9rem;
  color: #111827;
  margin-top: 2px;
}

.checkboxes {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

.checkboxes label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #374151;
  margin: 0;
}

.checkboxes input[type="checkbox"] {
  accent-color: #2563eb;
}

.buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-primary {
  flex: 1;
  padding: 10px 14px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  padding: 10px 14px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.results-panel {
  margin-top: 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 20px;
}

.results-panel h3 {
  margin: 0 0 14px 0;
  font-size: 1.1rem;
  color: #111827;
}

.results-table-wrapper {
  overflow-x: auto;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.results-table th,
.results-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.results-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.results-table td {
  color: #4b5563;
}

.results-table .positive {
  color: #059669;
  font-weight: 600;
}

.results-table .negative {
  color: #dc2626;
  font-weight: 600;
}

.results-table .zero {
  color: #6b7280;
}

.formula-box {
  margin-top: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #2563eb;
  font-size: 0.9rem;
  color: #374151;
}

.formula {
  display: block;
  margin-top: 6px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.85rem;
  color: #111827;
  background: #eef2ff;
  padding: 8px;
  border-radius: 6px;
  overflow-x: auto;
}
</style>
